import { useAppSelector } from 'hooks/redux';
import type { OrderedMap } from 'immutable';
import React, { memo, useLayoutEffect, useMemo, useRef, useState, type JSX } from 'react';

import EditNote from '@mui/icons-material/EditNote';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import InfoIcon from '@mui/icons-material/Info';
import ReceiptLong from '@mui/icons-material/ReceiptLong';
import { Button, Chip, Divider, Tooltip, Typography } from '@mui/material';

import ExcludeFromReports from 'assets/icons/settings-menu/ExcludeFromReports.inline.svg';
import ExcludeFromSpendingPlan from 'assets/icons/settings-menu/ExcludeFromSpendingPlan.inline.svg';

import { businessSelectors } from 'app-components/flux/business';
import { chartOfAccountsSelectors, chartOfAccountsUtils } from 'app-components/flux/chart-of-accounts';
import type {
  ChartOfAccountNodeProps,
  ChartOfAccountProps,
} from 'app-components/flux/chart-of-accounts/chartOfAccountsTypes';
import { customersSelectors } from 'app-components/flux/customers';
import { entitlementsSelectors } from 'app-components/flux/entitlements';
import { projectsSelectors } from 'app-components/flux/projects';
import type { Tag } from 'app-components/flux/tags/tagsTypes';
import type { SplitItemProps, TransactionProps } from 'app-components/flux/transactions/transactionsTypes';
import { tracker } from 'app-components/utils/core';

import QAmountField from 'components/QAmountField';
import UsageAndEntityIcon from 'components/UsageAndEntityIcon';

import { lookupTagName } from 'data/transactions/utils';
import { OtherSideField } from '../Transactions/DetailsDialog/fields';
import { isOverflowSplitItem, splitItemsToArray } from 'app-components/flux/transactions/splitsHelpers';
import { useSplitsViewStyles } from './splitsViewStyles';
import { transactionsSelectors, transactionsUtils } from 'app-components/flux/transactions';
import { getLength } from 'app-components/utils/core/immutableHelpers';

const getCoaNode = (
  coaId: string,
  coaNodesById: OrderedMap<string, ChartOfAccountNodeProps>,
  value?: ChartOfAccountProps | null,
) => {
  let ret = coaNodesById ? coaNodesById.get(coaId) : null;

  if (chartOfAccountsUtils.isTransferCoaNotFound(value)) {
    ret = coaNodesById.get(chartOfAccountsUtils.TRANSFER_NOT_FOUND_COA_ID);
  }
  return ret;
};

type SplitLineMemoProps = {
  memo: string;
  rowIndex: number;
  seriesVariant: boolean;
};

const SplitLineMemo = ({ memo, rowIndex, seriesVariant }: SplitLineMemoProps): JSX.Element | null => {
  const { classes, cx } = useSplitsViewStyles();
  const [expanded, setExpanded] = useState(false);
  const [canToggle, setCanToggle] = useState(false);
  const textRef = useRef<HTMLDivElement>(null);
  const trimmed = memo.trim();

  useLayoutEffect(() => {
    if (!trimmed) {
      setCanToggle(false);
      return;
    }
    if (expanded) {
      setCanToggle(true);
      return;
    }
    let cancelled = false;
    const raf1 = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (cancelled) return;
        const el = textRef.current;
        if (!el) {
          setCanToggle(false);
          return;
        }
        setCanToggle(el.scrollHeight > el.clientHeight + 1);
      });
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf1);
    };
  }, [trimmed, expanded]);

  if (!trimmed) return null;

  return (
    <div
      id={`${rowIndex}-split-row-memo`}
      className={cx(classes.splitRowMemo, seriesVariant && classes.splitRowMemoSeries)}
    >
      <EditNote
        aria-hidden
        sx={(theme) => ({
          fontSize: 24,
          flexShrink: 0,
          color: theme.palette.text.secondary,
        })}
      />
      <Typography
        ref={textRef}
        id={`${rowIndex}-split-row-memo-text`}
        component='div'
        variant='paragraphRegular'
        className={cx(classes.memoText, !expanded && classes.memoLineClamp)}
      >
        {trimmed}
      </Typography>
      {canToggle ? (
        <Button
          id={`${rowIndex}-split-row-memo-toggle`}
          variant='text'
          color='primary'
          className={classes.memoToggle}
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? 'Less' : 'More'}
        </Button>
      ) : null}
    </div>
  );
};

type Props = {
  items: SplitItemProps[];
  txnId?: string | null;
  goToOtherSide?: (txn: TransactionProps) => void;
  variant: 'default' | 'series';
};

const SplitItemsList = ({ items, txnId, goToOtherSide, variant }: Props): JSX.Element => {
  const { classes, cx } = useSplitsViewStyles();
  const isSeriesVariant = variant === 'series';
  const [showAll, setShowAll] = useState(false);
  const [showAllOptionAvailable, setShowAllOptionAvailable] = useState(false);

  const coaIDs = useAppSelector((state) => chartOfAccountsSelectors.getChartOfAccountNodesById(state));
  const businessEnabled = useAppSelector(entitlementsSelectors.getBusinessEnabled);
  const projects = useAppSelector(projectsSelectors.getProjects);
  const customers = useAppSelector(customersSelectors.getCustomers);
  const businesses = useAppSelector(businessSelectors.getResourcesById);
  const txn = useAppSelector((state) => transactionsSelectors.getTransactionById(state, txnId ?? ''));

  const displayItems = useMemo(() => {
    const isMatched = txn ? transactionsUtils.getMatchState(txn) === 'matched' : false;
    const txnsToShow = isMatched ? (splitItemsToArray(txn?.split?.items) ?? []) : items;
    setShowAllOptionAvailable(txnsToShow.length > 4);
    return showAll ? txnsToShow : txnsToShow.slice(0, 4);
  }, [txn, items, showAll]);

  const renderExclusionCluster = (
    showSp: boolean,
    showReports: boolean,
    options: { index: number; useSmallIcons: boolean },
  ): JSX.Element | null => {
    const { index, useSmallIcons } = options;
    if (!showSp && !showReports) return null;
    const iconClass = useSmallIcons ? classes.exclusionIconSmall : classes.excludeIcon;
    return (
      <div id={`${index}-split-row-exclusion-cluster`} className={useSmallIcons ? classes.exclusionCluster : undefined}>
        {showSp ? (
          <Tooltip title='Excluded from Spending Plan'>
            <ExcludeFromSpendingPlan className={iconClass} id={`split-sp-icon-${index}`} />
          </Tooltip>
        ) : null}
        {showReports ? (
          <Tooltip title='Excluded from Reports'>
            <ExcludeFromReports className={iconClass} id={`split-reports-icon-${index}`} />
          </Tooltip>
        ) : null}
      </div>
    );
  };

  return (
    <div
      id={`split-items-list--${variant}`}
      key='splitView'
      className={cx(classes.splitPadding, isSeriesVariant && classes.splitPaddingSeries)}
    >
      {displayItems.map((splitItem, index) => {
        const coaId = chartOfAccountsUtils.getCoaId(splitItem.coa);
        const catName = getCoaNode(coaId, coaIDs, splitItem.coa)?.fullPathNameWithoutUsageType;
        const business = businesses.get(splitItem.businessId ?? '');
        const isOverflow = isOverflowSplitItem(splitItem);
        const supportedTransfer =
          txnId &&
          splitItem?.coa &&
          splitItem.coa.type === 'ACCOUNT' &&
          chartOfAccountsUtils.isSupportedTransferCoa(splitItem.coa);
        const splitLineMustBeExcluded = Boolean(chartOfAccountsUtils.isTransferCOA(splitItem.coa));
        const showExcludeFromSpendingPlanIcon = splitItem.isExcludedFromF2S ?? splitLineMustBeExcluded;
        const showExcludeFromReportsIcon = splitItem.isExcludedFromReports ?? splitLineMustBeExcluded;

        const customer = businessEnabled && splitItem.customerId ? customers?.get(splitItem.customerId) : undefined;
        const customerDisplayName = customer
          ? customer.businessName || `${customer.firstName} ${customer.lastName}`
          : '';
        const project = businessEnabled && splitItem.projectId ? projects.get(splitItem.projectId) : undefined;
        const projectName = project?.name ?? '';
        const showClientProject = Boolean(customer || project);

        const useSmallExclusionIcons = true;

        const renderAmountBlock = (): JSX.Element => {
          if (isOverflow && splitItem.amount === 0) {
            return (
              <div id={`${index}-split-row-overflow-amount`} className={classes.overflowAmount}>
                {renderExclusionCluster(showExcludeFromSpendingPlanIcon, showExcludeFromReportsIcon, {
                  index,
                  useSmallIcons: useSmallExclusionIcons,
                })}
                <Tooltip title='Any extra amount is assigned to this Split.'>
                  <InfoIcon className={classes.infoIcon} />
                </Tooltip>
                <Typography variant='bodyRegular'>Overflow</Typography>
              </div>
            );
          }

          return (
            <div id={`${index}-split-row-amount-row`} className={classes.splitRowAmountInline}>
              {renderExclusionCluster(showExcludeFromSpendingPlanIcon, showExcludeFromReportsIcon, {
                index,
                useSmallIcons: useSmallExclusionIcons,
              })}
              <QAmountField
                margin='none'
                classes={{ input: classes.inputField }}
                textFieldVariant='filled'
                editable={false}
                plain
                value={splitItem.amount ? splitItem.amount : 0}
                id={`split-${index}-amount`}
                showSign='positive'
                showColor='positive'
              />
            </div>
          );
        };

        const row1Category = (
          <div id={`${index}-split-row-category-col`} className={classes.splitRowCategoryCol}>
            {!isSeriesVariant && businessEnabled ? (
              <div className={classes.padding}>
                <UsageAndEntityIcon
                  color={business?.color ?? ''}
                  size='xsmall'
                  usageIcon={splitItem.businessId ? (business?.taxGroup ?? 'BUSINESS') : 'PERSONAL'}
                />
              </div>
            ) : null}
            <div id={`${index}-split-row-category-stack`} className={classes.splitRowCategoryStack}>
              <Typography variant='bodyRegular' className={classes.categoryText}>
                {catName}
              </Typography>
              {supportedTransfer && goToOtherSide ? (
                <OtherSideField
                  coa={splitItem.coa}
                  txnId={txnId}
                  transferId={splitItem?.transfer?.id}
                  altAmount={splitItem.amount ?? 0}
                  goToOtherSide={goToOtherSide}
                  isSplit
                />
              ) : null}
            </div>
          </div>
        );

        const hasClientProjectCol = businessEnabled && showClientProject;
        const row1ClientProject = hasClientProjectCol ? (
          <div id={`${index}-split-row-client-project`} className={classes.splitRowClientProjectCol}>
            {customer ? <Typography variant='bodyMedium'>{customerDisplayName}</Typography> : null}
            {projectName ? <Typography variant='paragraphRegular'>{projectName}</Typography> : null}
          </div>
        ) : null;

        const row1Amount = (
          <div id={`${index}-split-row-amount-column`} className={classes.splitRowAmountCol}>
            {renderAmountBlock()}
            {businessEnabled && splitItem.isBillable ? (
              <Chip
                id={`${index}-split-row-billable`}
                icon={<ReceiptLong sx={{ fontSize: 16 }} />}
                label='Billable'
                size='small'
                className={classes.billableChip}
              />
            ) : null}
          </div>
        );

        const row2Tags =
          getLength(splitItem?.tags) > 0 ? (
            <div
              id={`${index}-split-row-tags`}
              className={cx(classes.splitRowTags, isSeriesVariant && classes.splitRowTagsSeries)}
            >
              {splitItem?.tags?.map((tag: Tag) => {
                const tagName = lookupTagName(tag);
                return <Chip key={tagName} label={tagName} className={classes.chip} />;
              })}
            </div>
          ) : null;

        const row3Memo =
          !isOverflow && splitItem.memo?.trim() ? (
            <SplitLineMemo rowIndex={index} memo={splitItem.memo ?? ''} seriesVariant={isSeriesVariant} />
          ) : null;

        return (
          <div key={splitItem.id || index} id={`${index}-split-row`}>
            <div id={`${index}-split-row-inner`} role='presentation'>
              <div className={cx(isSeriesVariant ? classes.splitItemSeriesBlock : classes.rowRootDetail)}>
                <div
                  id={`${index}-split-row-line-1`}
                  className={cx(
                    classes.splitRowLine1,
                    hasClientProjectCol ? classes.splitRowLine1ThreeCol : classes.splitRowLine1TwoCol,
                  )}
                >
                  {row1Category}
                  {row1ClientProject}
                  {row1Amount}
                </div>
                {row2Tags}
                {row3Memo}
              </div>
            </div>
            {(index !== displayItems.length - 1 || showAll) && <Divider sx={{ mt: 1.5, mx: 1 }} />}
          </div>
        );
      })}

      {showAllOptionAvailable && (
        <div id='split-items-list-show-all'>
          {!showAll && <Divider />}
          <Button
            id='show-all-split-row'
            className={classes.showAllButton}
            onClick={() => {
              tracker.track(tracker.events.splitsV2ShowMore, { willShow: showAll ? 'less' : 'more' });
              setShowAll(!showAll);
            }}
          >
            Show {showAll ? 'Less' : 'All'}
            {showAll ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </Button>
        </div>
      )}
    </div>
  );
};

export default memo(SplitItemsList);
